import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    POSTGRES_URL: z.string().url(),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    AUTH_SECRET:
      process.env.NODE_ENV === "production"
        ? z.string()
        : z.string().optional(),
    BETTER_AUTH_URL: z.string().url(),
    AUTH_URL: z.preprocess(
      // This makes Vercel deployments not fail if you don't set NEXTAUTH_URL
      // Since NextAuth.js automatically uses the VERCEL_URL if present.
      (str) => process.env.VERCEL_URL ?? str,
      // VERCEL_URL doesn't include `https` so it cant be validated as a URL
      process.env.VERCEL ? z.string() : z.string().url(),
    ),
    AUTH_GOOGLE_ID: z.string(),
    AUTH_GOOGLE_SECRET: z.string(),
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    STRIPE_DEFAULT_PRICE_ID: z.string(), // Fallback if tier has no Stripe ID configured
    STRIPE_DEFAULT_ANNUAL_PRICE_ID: z.string(), // Fallback if tier has no Stripe ID configured
    STRIPE_GUIA_ABDOMEN_PRICE_ID: z.string().optional(),
    STRIPE_GUIA_ABDOMEN_DISCOUNT_ID: z.string().optional(),
    PUBLITIO_API_KEY: z.string(),
    PUBLITIO_API_SECRET: z.string(),
    RESEND_API_KEY: z.string(),
    RESEND_WEBHOOK_SECRET: z.string().optional(),
    RESEND_NEWSLETTER_SEGMENT_ID: z.string().optional(),
    R2_ACCESS_KEY_ID: z.string(),
    R2_SECRET_ACCESS_KEY: z.string(),
    R2_BUCKET_NAME: z.string(),
    R2_SUPPORT_BUCKET_NAME: z.string(),
    R2_RECIPE_MEDIA_BUCKET_NAME: z.string(),
    R2_ENDPOINT: z.string().url(),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string(),
    R2_COURSE_MEDIA_BUCKET_NAME: z.string(),
    R2_EXERCISE_MEDIA_BUCKET_NAME: z.string(),
    R2_BLOG_MEDIA_BUCKET_NAME: z.string(),
    R2_GENERAL_MEDIA_BUCKET_NAME: z.string(),
    FB_CAPI_ACCESS_TOKEN: z.string().optional(),
    META_TEST_EVENT_CODE: z.string().optional(),
    CRON_SECRET: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    // NEXT_PUBLIC_CLIENTVAR: z.string(),
    NEXT_PUBLIC_BASE_URL: z.string().url(),
    NEXT_PUBLIC_R2_PUBLIC_URL_BASE: z.string().url(),
    NEXT_PUBLIC_R2_PUBLIC_RECIPES_URL_BASE: z.string().url(),
    NEXT_PUBLIC_R2_PUBLIC_COURSES_URL_BASE: z.string().url(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
    NEXT_PUBLIC_FB_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_R2_PUBLIC_GENERAL_MEDIA_URL_BASE: z.string().url(),
    NEXT_PUBLIC_R2_PUBLIC_EXERCISE_MEDIA_URL_BASE: z.string().url(),
    NEXT_PUBLIC_R2_PUBLIC_BLOG_MEDIA_URL_BASE: z.string().url(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    POSTGRES_URL: process.env.POSTGRES_URL,
    NODE_ENV: process.env.NODE_ENV,
    AUTH_SECRET: process.env.AUTH_SECRET,
    BETTER_AUTH_URL: process.env.BETTER_AUTH_URL,
    AUTH_URL: process.env.AUTH_URL,
    AUTH_GOOGLE_ID: process.env.AUTH_GOOGLE_ID,
    AUTH_GOOGLE_SECRET: process.env.AUTH_GOOGLE_SECRET,
    NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    NEXT_PUBLIC_R2_PUBLIC_URL_BASE: process.env.NEXT_PUBLIC_R2_PUBLIC_URL_BASE,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_DEFAULT_PRICE_ID: process.env.STRIPE_DEFAULT_PRICE_ID,
    STRIPE_DEFAULT_ANNUAL_PRICE_ID: process.env.STRIPE_DEFAULT_ANNUAL_PRICE_ID,
    STRIPE_GUIA_ABDOMEN_PRICE_ID: process.env.STRIPE_GUIA_ABDOMEN_PRICE_ID,
    STRIPE_GUIA_ABDOMEN_DISCOUNT_ID: process.env.STRIPE_GUIA_ABDOMEN_DISCOUNT_ID,
    PUBLITIO_API_KEY: process.env.PUBLITIO_API_KEY,
    PUBLITIO_API_SECRET: process.env.PUBLITIO_API_SECRET,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    RESEND_WEBHOOK_SECRET: process.env.RESEND_WEBHOOK_SECRET,
    RESEND_NEWSLETTER_SEGMENT_ID: process.env.RESEND_NEWSLETTER_SEGMENT_ID,
    R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID,
    R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY,
    R2_BUCKET_NAME: process.env.R2_BUCKET_NAME,
    R2_SUPPORT_BUCKET_NAME: process.env.R2_SUPPORT_BUCKET_NAME,
    R2_RECIPE_MEDIA_BUCKET_NAME: process.env.R2_RECIPE_MEDIA_BUCKET_NAME,
    R2_ENDPOINT: process.env.R2_ENDPOINT,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    NEXT_PUBLIC_R2_PUBLIC_RECIPES_URL_BASE:
      process.env.NEXT_PUBLIC_R2_PUBLIC_RECIPES_URL_BASE,
    NEXT_PUBLIC_R2_PUBLIC_COURSES_URL_BASE:
      process.env.NEXT_PUBLIC_R2_PUBLIC_COURSES_URL_BASE,
    R2_COURSE_MEDIA_BUCKET_NAME: process.env.R2_COURSE_MEDIA_BUCKET_NAME,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_FB_PIXEL_ID: process.env.NEXT_PUBLIC_FB_PIXEL_ID,
    NEXT_PUBLIC_R2_PUBLIC_GENERAL_MEDIA_URL_BASE:
      process.env.NEXT_PUBLIC_R2_PUBLIC_GENERAL_MEDIA_URL_BASE,
    NEXT_PUBLIC_R2_PUBLIC_EXERCISE_MEDIA_URL_BASE:
      process.env.NEXT_PUBLIC_R2_PUBLIC_EXERCISE_MEDIA_URL_BASE,
    R2_EXERCISE_MEDIA_BUCKET_NAME: process.env.R2_EXERCISE_MEDIA_BUCKET_NAME,
    R2_BLOG_MEDIA_BUCKET_NAME: process.env.R2_BLOG_MEDIA_BUCKET_NAME,
    R2_GENERAL_MEDIA_BUCKET_NAME: process.env.R2_GENERAL_MEDIA_BUCKET_NAME,
    NEXT_PUBLIC_R2_PUBLIC_BLOG_MEDIA_URL_BASE:
      process.env.NEXT_PUBLIC_R2_PUBLIC_BLOG_MEDIA_URL_BASE,
    FB_CAPI_ACCESS_TOKEN: process.env.FB_CAPI_ACCESS_TOKEN,
    META_TEST_EVENT_CODE: process.env.META_TEST_EVENT_CODE,
    CRON_SECRET: process.env.CRON_SECRET,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
